<template>
    <div class="w-auto">
        <div class="m-3">
            <div class="bg-white d-flex">
                <v-card-title class="p-0">{{ $t("res_Label_CorrespondancesList") }}</v-card-title>
                <v-spacer></v-spacer>
                <v-btn class="close-btn light-btn" icon dark @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <hr class="mb-3 mt-4">
            <!-- Filters -->
            <div class="d-flex align-items-md-end">
                <div class="p-0 col-md mt-1 d-flex" v-for="(filter, index) in filterConfigs" :key="index">
                    <div class="mt-2 col-12 pl-1 pr-1 pt-0 pb-0">
                        <label class="mb-1">{{ $t(filter.label) }}</label>
                        <StructureUserSelector class="w-100"
                                               v-bind="filter.props"
                                               :selectedStructureUsersList="filter.model"
                                               @structureUserSelectChanged="filter.method"
                                               :addressBook_header="$t(filter.header)" />
                    </div>
                </div>
                <v-btn dense @click="getAllTransfers()" class="primary mr-2">{{ $t("res_ButtonText_Search") }}</v-btn>
                <v-btn dense @click="clearAllFilters()" class="light-blue mr-2">{{ $t("res_Label_ClearAll") }}</v-btn>
            </div>

            <!-- Data Table -->
            <div class="mt-4 position-relative sm-margin">
                <v-data-table :mobile-breakpoint="0"
                              fixed-header
                              :height="tableHeight"
                              :headers="grid_headers"
                              :items="correspondences"
                              item-key="transferId"
                              hide-default-footer
                              :items-per-page="correspondences.length"
                              class="elevation-1 empty-full-height"
                              :item-class="getRowClass"
                              @dblclick:row="openFullView">
                    <!-- Table Slots (pinned, overdue, status, purpose, etc.) -->
                    <template v-slot:item.pinned="{ item }">
                        <v-icon @click="item.pinned = !item.pinned">{{ getPinned(item) }}</v-icon>
                    </template>

                    <template v-slot:item.overdue="{ item }">
                        <label class="float-end text-warning mx-2 d-flex">
                            <v-icon>{{ utils.getOverDue(item.overdue, nodeId) }}</v-icon>
                            {{ utils.getOverdueDays(item.overdue, item.overdueDays, nodeId) }}
                        </label>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <v-chip dark :class="getStatusClass(item)" text-color="black">{{ item.status }}</v-chip>
                    </template>

                    <template v-slot:item.purpose="{ item }">
                        <v-chip dark :color="getPurposeColor(item)" text-color="black">{{ item.purpose }}</v-chip>
                    </template>

                    <template v-slot:item.hasAttachment="{ item }">
                        <v-icon>{{ getAttachment(item.hasAttachment) }}</v-icon>
                    </template>

                    <template v-slot:item.subject="{ item }">
                        <span v-tooltip="item.subject.length > 50 ? item.subject : ''">
                            {{ utils.trimText(item.subject, 50) }}
                        </span>
                    </template>

                    <template v-slot:item.sender="{ item }">
                        <span class="text-limit" v-tooltip="item.sender">{{ item.sender }}</span>
                    </template>

                    <template v-slot:item.receivedDate="{ item }">
                        {{ constants.showDateWithTime ? utils.formatDateTimeWithTime(item.receivedDate) : utils.formatDateTime(item.receivedDate) }}
                    </template>

                    <template v-slot:item.createdDate="{ item }">
                        {{ utils.formatDateTime(item.createdDate) }}
                    </template>

                    <template v-slot:item.dueDate="{ item }">
                        {{ utils.formatDateTime(item.dueDate) }}
                    </template>

                    <template v-slot:no-data>
                        <div class="flex-1 text-center">
                            <img src="/assets/images/empty-inbox.svg" alt="" />
                            <p class="m-0">
                                {{ $t("res_Msgs_GetAllCaughtUp") }}<br />{{ $t("res_Msgs_NoNewItem") }}
                            </p>
                        </div>
                    </template>
                </v-data-table>
            </div>
        </div>
        
        <v-dialog
            v-model="showCorrespondenceDialog"
            content-class="create-document-dialog fullscreen-dialog corr_dialog"
            fullscreen
            :persistent="true"
        >
            <div id="correspondenceDialogEmptyDiv"></div>
            <CreateDocumentView
                v-if="showCorrespondenceDialog"
                :key="createDocumentViewComponentKey"
                :documentId="dialogDocumentId"
                :transferId="dialogTransferId"
                :viewMode="dialogViewMode"
                :delegationId="dialogDelegationId"
                :toEmail="dialogToEmail"
                :emailCC="dialogEmailCC"
                :isOpenedFromAdvancedSearch="true"
                :closeDialogEvent="closeCorrespondenceDialog"
                :openedFromNodeId="nodeId"
            />
        </v-dialog>
    </div>

</template>

<script>
    import { defaultGet, defaultPost } from '@/assets/js/common.js'
    import constants from '@/assets/js/constants.js'
    import axios from 'axios'
    import { getTransferPurposes } from '@/store/modules/lookups.js'
    import MultiSelect from 'vue-multiselect'
    import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed.js'
    import * as utils from '@/assets/js/common.js'
    import StructureUserSelector from '@/components/StructureUserSelector.vue'
    import CreateDocumentView from '@/views/CreateDocumentView.vue'

    export default {
        name: 'TransfersItems',
        components: { MultiSelect, VuePdfEmbed, StructureUserSelector, CreateDocumentView },
        props: {
            documentId: { type: [String, Number], default: null },
            dialogModelShow: { type: Boolean, default: false },
            nodeId: { type: Number, default: 0 },
            selected_node: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                documentIdLocal: this.documentId,
                lang: '',
                constants: constants,
                correspondences: [],
                selected: [],
                utils: utils,
                tableHeight: 'calc(100vh - 16em)',
                fromStructures: [],
                toStructures: [],
                fromUsers: [],
                toUsers: [],
                documentRecipients: [],
                showCorrespondenceDialog: false,
                dialogDocumentId: null,
                dialogTransferId: null,
                dialogDelegationId: 0,
                dialogViewMode: 'readonly',
                dialogToEmail: '',
                dialogEmailCC: '',
                selectedItem: { item: null, value: false },
                createDocumentViewComponentKey: 0,
                grid_headers: [
                    { text: window.translate('res_GridHeaders_CreatedSubject'), value: 'subject', sortable: false },
                    { text: window.translate('res_MenuItems_ReferenceNumber'), value: 'referenceNumber', sortable: false },
                    { text: '', value: 'Overdue', sortable: false },
                    { text: window.translate('res_GridHeaders_DueDate'), value: 'dueDate', sortable: false },
                    { text: window.translate('res_Label_CreatedBy'), value: 'createdBy', sortable: false },
                    { text: window.translate('res_GridHeaders_FromUser'), value: 'fromUser', sortable: false },
                    { text: window.translate('res_GridHeaders_FromStructure'), value: 'sender', sortable: false },
                    { text: window.translate('res_GridHeaders_ToUser'), value: 'toUser', sortable: false },
                    { text: window.translate('res_GridHeaders_ToStructure'), value: 'toStructure', sortable: false },
                    { text: window.translate('res_Label_DocumentSender'), value: 'sendingEntity', sortable: false },
                    { text: window.translate('res_Label_DocumentRecipients'), value: 'receiversEntities', sortable: false },
                    { text: window.translate('res_Label_Privacy'), value: 'privacy', sortable: false },
                    { text: window.translate('res_Label_Priority'), value: 'priority', sortable: false },
                    { text: window.translate('res_GridHeaders_ActionType'), value: 'purpose', sortable: false },
                    { text: window.translate('res_GridHeader_Status'), value: 'status', sortable: false },
                    { text: window.translate('res_GridHeader_Category'), value: 'category', sortable: false },
                    { text: window.translate('res_GridHeaders_Recieved'), value: 'receivedDate', sortable: false },
                    { text: '', value: 'actions', sortable: false },
                    { text: '', value: 'transfersItems', sortable: false },
                ],
                purposes: getTransferPurposes(),
            }
        },
        mounted() {
            this.lang = localStorage.getItem('lang')
            this.clearAllFilters()
        },
        computed: {
            filterConfigs() {
                return [
                    {
                        label: 'res_GridHeaders_FromStructure',
                        props: { showInternalStructures: true, showInternalUsers: false, showExternalStructures: false, showExternalUsers: false, excludeSelf: false, isMultiple: true },
                        model: this.fromStructures,
                        method: (val) => this.setStructure(val, 1),
                        header: 'res_Label_SelectStructure',
                    },
                    {
                        label: 'res_GridHeaders_ToStructure',
                        props: { showInternalStructures: true, showInternalUsers: false, showExternalStructures: false, showExternalUsers: false, excludeSelf: false, isMultiple: true },
                        model: this.toStructures,
                        method: (val) => this.setStructure(val, 2),
                        header: 'res_Label_SelectStructure',
                    },
                    {
                        label: 'res_GridHeaders_FromUser',
                        props: { showInternalStructures: false, showInternalUsers: true, showExternalStructures: false, showExternalUsers: false, excludeSelf: false, isMultiple: true },
                        model: this.fromUsers,
                        method: (val) => this.setUser(val, 1),
                        header: 'SelectUser',
                    },
                    {
                        label: 'res_GridHeaders_ToUser',
                        props: { showInternalStructures: true, showInternalUsers: true, showExternalStructures: false, showExternalUsers: false, excludeSelf: false, isMultiple: true },
                        model: this.toUsers,
                        method: (val) => this.setUser(val, 2),
                        header: 'SelectUser',
                    },
                    {
                        label: 'res_Label_DocumentRecipients',
                        props: { showInternalStructures: true, showInternalUsers: false, showExternalStructures: true, showExternalUsers: false, excludeSelf: false, isMultiple: true },
                        model: this.documentRecipients,
                        method: (val) => this.setStructure(val, 4),
                        header: 'res_Label_SelectStructure',
                    },
                ]
            },
        },
        methods: {
            getAllTransfers() {
                const query = this.prepareSearchQuery()
                window.showLoading();
                axios
                    .post(constants.baseApiUrl + 'Transfer/TransfersListForDocument', query)
                    .then((res) => {
                        if (!res?.data) return window.showAlert('res_Msgs_ErrorOccured', 'error')
                        this.correspondences = res.data

                    })
                    .catch(console.log)
                    .finally(() => window.hideLoading())
            },
            setStructure(items, type) {
                items = utils.handleFirstElement(items)
                switch (type) {
                    case 1:
                        this.fromStructures = items || []
                        break
                    case 2:
                        this.toStructures = items || []
                        break
                    case 4:
                        this.documentRecipients = items || []
                        break
                }
            },
            setUser(items, type) {
                items = utils.handleFirstElement(items)
                switch (type) {
                    case 1:
                        this.fromUsers = items || []
                        break
                    case 2:
                        this.toUsers = items || []
                        break
                }
            },
            getRowClass(item) {
                var retVal = ''
                if (item.openedDate || item.closedDate || item.openedForPreview || this.nodeId != 2)
                    retVal = ''
                else
                    retVal = 'tr-bold'

                if (this.selectedItem != null && this.selectedItem.item != null && item == this.selectedItem.item) {
                    this.selectedItem.value = true
                    retVal += 'custom-class'
                }

                return retVal
            },
            itemSelected(item, value) {
                if (item.value == true) {
                    this.selectedItem = item
                }
                else {
                    this.selectedItem = null
                }
            },
            openFullView(event, bag) {
                // Open the correspondence in the full CreateDocumentView dialog when a row is double-clicked.
                let item = bag.item;
                if (!item) return

                this.selectedItem = { item, value: true }

                const isDraft = item.statusId === 1
                const fallbackDelegationId = this.selected_node?.delegationId || 0

                this.dialogViewMode = isDraft ? 'edit_new' : 'readonly'
                this.dialogDocumentId = isDraft ? item.id : (item.documentId || item.id)
                this.dialogTransferId = isDraft ? null : item.transferId || null
                this.dialogDelegationId = isDraft ? 0 : (item.delegationId || fallbackDelegationId)
                this.dialogToEmail = item.sentToEmail || ''
                this.dialogEmailCC = item.sentToManagerEmail || ''
                this.createDocumentViewComponentKey = (this.createDocumentViewComponentKey || 0) + 1

                this.showCorrespondenceDialog = true
            },
            getStatusClass(item) {
                if (constants.statusesEnum.completed.value == item.statusId)
                    return constants.statusesEnum.completed?.data()?.classColor;

                return '';
            },
            getPurposeColor(item) {
                var purposeItem = this.purposes.find(x => x.id == item.purposeId)
                if (purposeItem != null)
                    return purposeItem.color
                return ''
            },
            closeDialog() {
                this.$emit('close-dialog')
            },
            closeCorrespondenceDialog() {
                this.showCorrespondenceDialog = false
                this.dialogTransferId = null
                this.dialogDocumentId = null
                this.dialogDelegationId = 0
                this.dialogViewMode = 'readonly'
                this.dialogToEmail = ''
                this.dialogEmailCC = ''
                this.selectedItem = { item: null, value: false }
                this.createDocumentViewComponentKey = 0
            },
            prepareSearchQuery() {
                const queryObject = {
                    delegationId: this.selected_node?.delegationId || 0,
                    documentId: this.documentIdLocal,

                    fromStructureIds: this.fromStructures.filter(x=> x.id > 0).map(item => item.id).join(',') || '',
                    toStructureIds: this.toStructures.filter(x=> x.id > 0).map(item => item.id).join(',') || '',
                    fromUserIds: this.fromUsers.filter(x=> x.id).map(item => item.id.includes('-') ? item.id.split('-')[0] : item.id).join(',') || '',
                    toUserIds: this.toUsers.filter(x=> x.id).map(item => item.id.includes('-') ? item.id.split('-')[0] : item.id).join(',') || '',
                    receipntsIds: this.documentRecipients.filter(x=> x.id > 0).map(item => item.id).join(',') || ''
                }
                return queryObject
            },
            clearAllFilters() {
                this.fromStructures = []
                this.toStructures = []
                this.fromUsers = []
                this.toUsers = []
                this.documentRecipients = []
                this.getAllTransfers()
            },
        },
    }
</script>

<style scoped>
    /* Add any necessary scoped styles */

    
</style>
